import { OrderStatusConfig } from 'components/commercetools-ui/organisms/account/types/types';
import { getParentLineItems, groupLineItemsBySku, separateMediaAndPrintBuilderProducts } from 'components/commercetools-ui/organisms/cart/helpers';
import { initialViewStatus } from 'components/commercetools-ui/organisms/order-details';
import {
  groupLineItemsByOrderStatus,
  groupLineItemsWithTrackingAndStatusSplit,
} from 'components/commercetools-ui/organisms/order-details/helpers/orderStatusMapper';
import { Order } from 'types/entity/order';

/**
 * Maps the raw order status from the backend (e.g., "OPEN", "INRENDERING", "SHIPPED")
 * to a user-friendly status (e.g., "Submitted", "Processing", "Shipped"),
 * based on the cart kind and the provided status configurations for P2R and M2H.
 */
export const getEofsToUserStatus = (cartKind: string, p2rStatus?: OrderStatusConfig, m2hStatus?: OrderStatusConfig) => {
  const eofsToUserP2RStatus: Record<string, string> = {
    OPEN: p2rStatus?.[0]?.submitted || 'Submitted',
    INRENDERING: p2rStatus?.[0]?.processing || 'Processing',
    MEDIARETRIEVED: p2rStatus?.[0]?.fulfillment || 'In Fulfillment',
    SHIPPED: p2rStatus?.[0]?.shipped || 'Ready for Pickup',
    PAID: p2rStatus?.[0]?.pickedUp || 'Order Picked Up',
    CANCELLED: p2rStatus?.[0]?.cancelled || 'Cancelled',
    HOLD: p2rStatus?.[0]?.hold || 'On Hold',
    DESTROYED: p2rStatus?.[0]?.destroyed || 'No Longer Available',
  };

  const eofsToUserM2HStatus: Record<string, string> = {
    OPEN: m2hStatus?.[0]?.submitted || 'Submitted',
    INRENDERING: m2hStatus?.[0]?.processing || 'Processing',
    MEDIARETRIEVED: m2hStatus?.[0]?.fulfillment || 'In Fulfillment',
    SHIPPED: m2hStatus?.[0]?.shipped || 'Shipped',
    CANCELLED: m2hStatus?.[0]?.cancelled || 'Cancelled',
    HOLD: m2hStatus?.[0]?.hold || 'On Hold',
    REFUNDED: m2hStatus?.[0]?.refunded || 'REFUND',
    REMAKE: m2hStatus?.[0]?.remake || 'REMAKE',
    DELIVERED: m2hStatus?.[0]?.delivered || 'Delivered',
  };

  return cartKind === 'm2hCart' ? eofsToUserM2HStatus : eofsToUserP2RStatus;
};

/**
 * Determines the least advanced order status for an order, based on its line items and the provided status mappings.
 * E.g., Order has 3 line items -> 1st line item is SUBMITTED, 2nd line item is PROCESSING, 3rd line item is INFULFILLMENT.
 * So, the least order status is SUBMITTED, so we return "Submitted".
 */
export const getLeastOrderStatus = (
  order: Order,
  cartKind: string,
  p2rStatus?: OrderStatusConfig,
  m2hStatus?: OrderStatusConfig,
) => {
  const eofsToUserStatus = getEofsToUserStatus(cartKind, p2rStatus, m2hStatus);

  // early return on order level status
  if( order?.orderState?.toUpperCase() === 'OPEN' || order?.orderState?.toUpperCase() === 'CANCELLED'){
    return eofsToUserStatus[order?.orderState?.toUpperCase()];
  }

  const parentLineItems = getParentLineItems(order?.lineItems??[]);
  const [mediaClipLineItems, printBuilderLineItems] = separateMediaAndPrintBuilderProducts(parentLineItems ?? []);
  const groupedSkus = [...groupLineItemsBySku(...printBuilderLineItems), ...mediaClipLineItems];
  const groupedOrderStatus = () => {
    if (order?.orderState && initialViewStatus.has(order.orderState.toUpperCase())) {
      return [
        {
          statusCode: order?.orderState,
          lineItems: groupedSkus,
          trackingDetails: undefined,
        },
      ];
    }
    if (cartKind === 'm2hCart') {
      return groupLineItemsWithTrackingAndStatusSplit(parentLineItems, order?.deliveries);
    } else {
      return groupLineItemsByOrderStatus(parentLineItems);
    }
  };

  const groupedOrder = groupedOrderStatus();
  const statusPriority = Object.keys(getEofsToUserStatus(cartKind, p2rStatus, m2hStatus));
  // Find all statusCodes in groupedOrder that are in statusPriority
  const validStatuses = groupedOrder
    .map((order) => order.statusCode?.toUpperCase())
    .filter((statusCode): statusCode is string => !!statusCode && statusPriority.includes(statusCode));
  // Return the one with the lowest index in statusPriority
  let leastStatus: string | undefined = undefined;
  let minIndex = Number.POSITIVE_INFINITY;
  for (const status of validStatuses) {
    const idx = statusPriority.indexOf(status);
    if (idx !== -1 && idx < minIndex) {
      minIndex = idx;
      leastStatus = status;
    }
  }

  return leastStatus ? eofsToUserStatus[leastStatus.toUpperCase()] : order.orderState;
};


export const getAddressFromOrder = (order: Order) => {
  const stateOrRegion = order.shippingAddress?.state || order.shippingAddress?.region || '';
  let address = '';

  if (order.shippingAddress?.streetName) address += `${order.shippingAddress.streetName},`;
  if (order.shippingAddress?.apartment) address += `\n${order.shippingAddress.apartment}`;
  if (order.shippingAddress?.city) address += `\n${order.shippingAddress.city},`;
  if (stateOrRegion) address += ` ${stateOrRegion}`;
  if (order.shippingAddress?.postalCode) address += ` ${order.shippingAddress.postalCode}`;
  return address;
};
